import { useEffect, useState, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ClipboardText, MagnifyingGlass, Export, Funnel, CaretDown, ArrowRight, ArrowLeft, ShieldCheck, ClockCounterClockwise, SortAscending, SortDescending } from '@phosphor-icons/react';
import axios from 'axios';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const actionColors = {
  LOGIN: 'bg-blue-50 text-blue-700 border-blue-200',
  LOGOUT: 'bg-stone-100 text-stone-600 border-stone-300',
  REGISTER: 'bg-green-50 text-green-700 border-green-200',
  CREATE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
  DISPENSE: 'bg-violet-50 text-violet-700 border-violet-200',
  MOVEMENT: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  ASSIGN: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const moduleIcons = {
  auth: 'text-blue-600', volunteers: 'text-green-600', studies: 'text-purple-600',
  clients: 'text-amber-600', samples: 'text-teal-600', warehouse: 'text-teal-600',
  users: 'text-stone-600', config: 'text-stone-500', study_visits: 'text-purple-500',
  study_volunteers: 'text-indigo-600', question_bank: 'text-pink-600',
  study_questionnaires: 'text-pink-500', appointments: 'text-orange-600',
  dispensations: 'text-violet-600', sample_movements: 'text-cyan-600',
  temperature_logs: 'text-red-500',
};

const PAGE_SIZE = 30;

export default function Audit() {
  const { lang } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [volTimeline, setVolTimeline] = useState(null);
  const [volTimelineLoading, setVolTimelineLoading] = useState(false);
  const [studyTimeline, setStudyTimeline] = useState(null);
  const [studyTimelineLoading, setStudyTimelineLoading] = useState(false);
  const [timelineOrder, setTimelineOrder] = useState('desc');
  // Autocompletado de voluntario por nombre en el filtro de recurso.
  const [volSuggest, setVolSuggest] = useState([]);
  const [showVolSuggest, setShowVolSuggest] = useState(false);
  const volSearchTimer = useRef(null);

  const onResourceChange = (val) => {
    setFilters(p => ({ ...p, resource_id: val })); setPage(0);
    if (volSearchTimer.current) clearTimeout(volSearchTimer.current);
    const q = (val || '').trim();
    if (q.length < 2) { setShowVolSuggest(false); setVolSuggest([]); return; }
    volSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/volunteers?search=${encodeURIComponent(q)}&limit=8`, { withCredentials: true });
        setVolSuggest(data?.data || []);
        setShowVolSuggest((data?.data || []).length > 0);
      } catch {
        setVolSuggest([]); setShowVolSuggest(false);
      }
    }, 300);
  };

  const pickVolunteer = (v) => {
    setFilters(p => ({ ...p, resource_id: v.volunteer_id })); setPage(0);
    setShowVolSuggest(false); setVolSuggest([]);
  };

  const [filters, setFilters] = useState({
    module: '', action: '', user_email: '', resource_id: '', study_id: '',
    date_from: '', date_to: '', search: '',
  });

  const lb = lang === 'es'
    ? { title: 'Auditoria', subtitle: 'Trazabilidad completa ALCOA+', records: 'registros', today: 'Hoy', total_records: 'Total registros', modules: 'Modulos', users: 'Usuarios', date: 'Fecha / Hora', user: 'Usuario', action: 'Accion', module: 'Modulo', resource: 'Recurso', ip: 'Direccion IP', details: 'Detalles', no_records: 'No hay registros de auditoria', loading: 'Cargando...', filters: 'Filtros', clear: 'Limpiar', export_csv: 'Exportar CSV', all: 'Todos', search: 'Buscar por texto...', date_from: 'Desde', date_to: 'Hasta', user_filter: 'Usuario', resource_filter: 'ID Recurso', study_filter: 'ID Estudio', detail_title: 'Detalle del registro', old_values: 'Valores anteriores', new_values: 'Valores nuevos', field: 'Campo', before: 'Antes', after: 'Despues', no_changes: 'Sin cambios registrados', page_of: 'de', immutable: 'Registro inmutable', alcoa_a: 'Atribuible', alcoa_l: 'Legible', alcoa_c: 'Contemporaneo', alcoa_o: 'Original', alcoa_ac: 'Exacto' }
    : { title: 'Audit', subtitle: 'Full ALCOA+ traceability', records: 'records', today: 'Today', total_records: 'Total records', modules: 'Modules', users: 'Users', date: 'Date / Time', user: 'User', action: 'Action', module: 'Module', resource: 'Resource', ip: 'IP Address', details: 'Details', no_records: 'No audit records', loading: 'Loading...', filters: 'Filters', clear: 'Clear', export_csv: 'Export CSV', all: 'All', search: 'Search text...', date_from: 'From', date_to: 'To', user_filter: 'User', resource_filter: 'Resource ID', study_filter: 'Study ID', detail_title: 'Record detail', old_values: 'Previous values', new_values: 'New values', field: 'Field', before: 'Before', after: 'After', no_changes: 'No changes recorded', page_of: 'of', immutable: 'Immutable record', alcoa_a: 'Attributable', alcoa_l: 'Legible', alcoa_c: 'Contemporaneous', alcoa_o: 'Original', alcoa_ac: 'Accurate' };

  const fetchStats = useCallback(() => {
    axios.get(`${API_URL}/api/audit/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(page * PAGE_SIZE) });
    if (filters.module) params.set('module', filters.module);
    if (filters.action) params.set('action', filters.action);
    if (filters.user_email) params.set('user_email', filters.user_email);
    if (filters.resource_id) params.set('resource_id', filters.resource_id);
    if (filters.study_id) params.set('study_id', filters.study_id);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.search) params.set('search', filters.search);
    axios.get(`${API_URL}/api/audit/logs?${params}`, { withCredentials: true })
      .then(r => { setLogs(r.data.logs || []); setTotal(r.data.total || 0); })
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({ module: '', action: '', user_email: '', resource_id: '', study_id: '', date_from: '', date_to: '', search: '' });
    setPage(0);
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.module) params.set('module', filters.module);
    if (filters.action) params.set('action', filters.action);
    if (filters.user_email) params.set('user_email', filters.user_email);
    if (filters.study_id) params.set('study_id', filters.study_id);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    window.open(`${API_URL}/api/audit/export?${params}`, '_blank');
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasActiveFilters = Object.values(filters).some(v => v);

  const openDetail = (log) => {
    axios.get(`${API_URL}/api/audit/logs/${log.id}`, { withCredentials: true })
      .then(r => setDetail(r.data))
      .catch(() => setDetail(log));
  };

  // Resuelve el appointment_id de un log de auditoria relacionado con citas.
  const APPT_EVENT_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'SEND_SMS_REMINDER'];
  const getApptId = (log) => {
    if (!log) return null;
    if (APPT_EVENT_ACTIONS.includes(log.action) && log.resource_id && (log.module === 'appointments' || log.module === 'esendex')) return log.resource_id;
    const ids = log.details?.appointment_ids;
    if (Array.isArray(ids) && ids.length) return ids[0];
    return null;
  };

  const openTimeline = (apptId) => {
    if (!apptId) return;
    setTimelineLoading(true);
    setTimeline({ appointment_id: apptId, appointment: null, events: [] });
    axios.get(`${API_URL}/api/audit/appointments/${apptId}/timeline`, { withCredentials: true })
      .then(r => setTimeline(r.data))
      .catch(() => setTimeline({ appointment_id: apptId, appointment: null, events: [] }))
      .finally(() => setTimelineLoading(false));
  };

  const eventSummary = (ev) => {
    if (ev.action === 'UPDATE') {
      const nv = ev.new_values || {};
      const parts = Object.entries(nv).filter(([k]) => k !== 'updated_at').map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
      return parts.length ? parts.join(', ') : (lang === 'es' ? 'Modificación' : 'Update');
    }
    const map = {
      CREATE: lang === 'es' ? 'Creación' : 'Created',
      DELETE: lang === 'es' ? 'Eliminación' : 'Deleted',
      SEND_SMS_REMINDER: lang === 'es' ? 'Recordatorio SMS enviado' : 'SMS reminder sent',
      APPT_EMAIL_SEND: lang === 'es' ? 'Recordatorio por email enviado' : 'Email reminder sent',
      BATCH_CREATE_APPOINTMENTS: lang === 'es' ? 'Alta/reprogramación por lote' : 'Batch create/reschedule',
      GENERATE_APPOINTMENTS: lang === 'es' ? 'Generación automática de citas' : 'Auto-generated appointments',
      ASSIGN_VOLUNTEER: lang === 'es' ? 'Voluntario asignado a estudio' : 'Volunteer assigned to study',
      INCLUDE_VOLUNTEER: lang === 'es' ? 'Voluntario incluido' : 'Volunteer included',
      REMOVE_VOLUNTEER: lang === 'es' ? 'Voluntario retirado del estudio' : 'Volunteer removed from study',
      UPDATE_VOLUNTEER_STATUS: lang === 'es' ? 'Cambio de estado del voluntario' : 'Volunteer status change',
      MARK_APTO: lang === 'es' ? 'Marcado como apto' : 'Marked as eligible',
      FILL_IE_QUESTIONNAIRE: lang === 'es' ? 'Criterios I/E rellenados' : 'I/E criteria filled',
      GENERATE_CONSENT: lang === 'es' ? 'Consentimiento generado' : 'Consent generated',
      SIGN_CONSENT: lang === 'es' ? 'Consentimiento firmado' : 'Consent signed',
      CONSENT_SIGNED_VIDSIGNER: lang === 'es' ? 'Consentimiento firmado (VIDsigner)' : 'Consent signed (VIDsigner)',
      VIDSIGNER_SEND: lang === 'es' ? 'Envío a firma (VIDsigner)' : 'Sent to signature (VIDsigner)',
      DISPENSATION: lang === 'es' ? 'Dispensación de producto' : 'Product dispensation',
      FAMILY_LINK: lang === 'es' ? 'Vínculo familiar añadido' : 'Family link added',
      FAMILY_UNLINK: lang === 'es' ? 'Vínculo familiar eliminado' : 'Family link removed',
      DELETE_VOLUNTEER_DOCUMENT: lang === 'es' ? 'Documento eliminado' : 'Document deleted',
      SUBMIT_SATISFACTION_SURVEY: lang === 'es' ? 'Encuesta de satisfacción enviada' : 'Satisfaction survey submitted',
      QSALUD_DENTAL_STATUS_UPDATED: lang === 'es' ? 'Estado dental Q-Salud actualizado' : 'Q-Salud dental status updated',
    };
    return map[ev.action] || `${ev.action} · ${ev.module || ''}`;
  };

  // Orden backend = DESC (más reciente primero). Si el usuario elige ASC, invertimos.
  const orderEvents = (events) => (timelineOrder === 'desc' ? events : [...events].reverse());

  // Muestra el id de la cita asociada a un evento (para trazabilidad: ver el id
  // de una cita borrada y el de la nueva). Clic => abre el historial de esa cita.
  const renderApptRef = (ev) => {
    const aid = getApptId(ev);
    if (!aid) return null;
    return (
      <button type="button" onClick={() => openTimeline(aid)} data-testid="timeline-event-appt-ref"
        className="mt-0.5 block text-[10px] font-mono text-[#1f3e80] hover:underline break-all text-left"
        title={lang === 'es' ? 'Ver historial de esta cita' : 'View this appointment timeline'}>
        {lang === 'es' ? 'Cita' : 'Appt'}: {aid}
      </button>
    );
  };
  const renderOrderToggle = (key) => (
    <button
      type="button"
      data-testid={`timeline-order-toggle-${key}`}
      onClick={() => setTimelineOrder(o => (o === 'desc' ? 'asc' : 'desc'))}
      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#1f3e80] hover:underline"
    >
      {timelineOrder === 'desc' ? <SortDescending size={14} /> : <SortAscending size={14} />}
      {timelineOrder === 'desc'
        ? (lang === 'es' ? 'Más reciente primero' : 'Newest first')
        : (lang === 'es' ? 'Más antiguo primero' : 'Oldest first')}
    </button>
  );

  const openVolTimeline = (volId) => {
    if (!volId) return;
    setVolTimelineLoading(true);
    setVolTimeline({ volunteer_id: volId, volunteer: null, events: [] });
    axios.get(`${API_URL}/api/audit/volunteers/${volId}/timeline`, { withCredentials: true })
      .then(r => setVolTimeline(r.data))
      .catch(() => setVolTimeline({ volunteer_id: volId, volunteer: null, events: [] }))
      .finally(() => setVolTimelineLoading(false));
  };

  const getVolId = (log) => {
    if (!log) return null;
    const fromRes = log.resource_id && String(log.resource_id).match(/VOL-\d+/i);
    if (fromRes) return fromRes[0];
    const cand = log.details?.volunteer_id || log.details?.volunteer;
    if (cand) {
      const m = String(cand).match(/VOL-\d+/i);
      if (m) return m[0];
    }
    return null;
  };

  const getStudyId = (log) => {
    if (!log) return null;
    const candidates = [log.resource_id, log.study_id, log.details?.study_id, log.details?.study, log.details?.study_code, log.new_values?.study_id, log.old_values?.study_id];
    for (const c of candidates) {
      if (c) { const m = String(c).match(/EST-\d+/i); if (m) return m[0]; }
    }
    return null;
  };

  const openStudyTimeline = (studyId) => {
    if (!studyId) return;
    setStudyTimelineLoading(true);
    setStudyTimeline({ study_id: studyId, study: null, events: [] });
    axios.get(`${API_URL}/api/audit/studies/${studyId}/timeline`, { withCredentials: true })
      .then(r => setStudyTimeline(r.data))
      .catch(() => setStudyTimeline({ study_id: studyId, study: null, events: [] }))
      .finally(() => setStudyTimelineLoading(false));
  };

  const renderDiff = (old_values, new_values) => {
    if (!old_values && !new_values) return <p className="text-xs text-stone-400 italic">{lb.no_changes}</p>;
    const allKeys = [...new Set([...Object.keys(old_values || {}), ...Object.keys(new_values || {})])];
    if (allKeys.length === 0) return <p className="text-xs text-stone-400 italic">{lb.no_changes}</p>;
    return (
      <div className="border border-stone-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs w-1/4">{lb.field}</TableHead>
              <TableHead className="text-xs w-[37.5%]">{lb.before}</TableHead>
              <TableHead className="text-xs w-[37.5%]">{lb.after}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allKeys.map(key => {
              const oldVal = old_values?.[key];
              const newVal = new_values?.[key];
              const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
              return (
                <TableRow key={key} className={changed ? 'bg-amber-50/40' : ''}>
                  <TableCell className="font-mono text-xs text-stone-700 font-medium">{key}</TableCell>
                  <TableCell className={`text-xs ${changed ? 'text-red-600 line-through' : 'text-stone-500'}`}>
                    {oldVal !== undefined && oldVal !== null ? (typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)) : '-'}
                  </TableCell>
                  <TableCell className={`text-xs ${changed ? 'text-green-700 font-medium' : 'text-stone-500'}`}>
                    {newVal !== undefined && newVal !== null ? (typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)) : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardText size={28} weight="duotone" className="text-[#1f3e80]" />
          <div>
            <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{lb.title}</h1>
            <p className="text-xs text-stone-500 font-mono mt-0.5">{lb.subtitle}</p>
          </div>
        </div>
        <Button variant="outline" data-testid="export-csv-btn" onClick={handleExport} className="rounded-sm gap-2 text-sm">
          <Export size={14} />{lb.export_csv}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-stats">
          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <p className="text-xs text-stone-500 font-body">{lb.total_records}</p>
            <p className="text-2xl font-heading font-semibold text-stone-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <p className="text-xs text-stone-500 font-body">{lb.today}</p>
            <p className="text-2xl font-heading font-semibold text-[#1f3e80] mt-1">{stats.today}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <p className="text-xs text-stone-500 font-body">{lb.modules}</p>
            <p className="text-2xl font-heading font-semibold text-stone-900 mt-1">{stats.modules?.length || 0}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <p className="text-xs text-stone-500 font-body">{lb.users}</p>
            <p className="text-2xl font-heading font-semibold text-stone-900 mt-1">{stats.users?.length || 0}</p>
          </div>
        </div>
      )}

      {/* ALCOA+ Indicators */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'A', label: lb.alcoa_a, desc: lang === 'es' ? 'Quien' : 'Who', color: 'bg-blue-50 text-blue-700 border-blue-200' },
          { key: 'L', label: lb.alcoa_l, desc: lang === 'es' ? 'Legible' : 'Legible', color: 'bg-green-50 text-green-700 border-green-200' },
          { key: 'C', label: lb.alcoa_c, desc: lang === 'es' ? 'Cuando' : 'When', color: 'bg-amber-50 text-amber-700 border-amber-200' },
          { key: 'O', label: lb.alcoa_o, desc: lang === 'es' ? 'Original' : 'Original', color: 'bg-purple-50 text-purple-700 border-purple-200' },
          { key: 'A+', label: lb.alcoa_ac, desc: lang === 'es' ? 'Exacto' : 'Accurate', color: 'bg-teal-50 text-teal-700 border-teal-200' },
        ].map(a => (
          <span key={a.key} className={`inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-sm border ${a.color}`}>
            <ShieldCheck size={12} weight="bold" /><strong>{a.key}</strong> {a.label} <span className="text-[10px] opacity-60">({a.desc})</span>
          </span>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-white border border-stone-200 rounded-sm">
        <button
          data-testid="audit-filters-toggle"
          onClick={() => setFiltersOpen(p => !p)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-stone-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Funnel size={14} className="text-stone-500" />
            <span className="text-sm font-medium text-stone-700">{lb.filters}</span>
            {hasActiveFilters && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{lang === 'es' ? 'Activos' : 'Active'}</Badge>}
          </div>
          <CaretDown size={14} className={`text-stone-400 transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`} />
        </button>

        {filtersOpen && (
          <div className="px-4 pb-4 border-t border-stone-100 pt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{lb.module}</Label>
                <Select value={filters.module || '_all'} onValueChange={v => { setFilters(p => ({ ...p, module: v === '_all' ? '' : v })); setPage(0); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filter-module"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{lb.all}</SelectItem>
                    {(stats?.modules || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.action}</Label>
                <Select value={filters.action || '_all'} onValueChange={v => { setFilters(p => ({ ...p, action: v === '_all' ? '' : v })); setPage(0); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filter-action"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{lb.all}</SelectItem>
                    {(stats?.actions || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.user_filter}</Label>
                <Select value={filters.user_email || '_all'} onValueChange={v => { setFilters(p => ({ ...p, user_email: v === '_all' ? '' : v })); setPage(0); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filter-user"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{lb.all}</SelectItem>
                    {(stats?.users || []).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.resource_filter}</Label>
                <div className="relative">
                  <Input className="h-8 text-xs" data-testid="filter-resource" value={filters.resource_id}
                    onChange={e => onResourceChange(e.target.value)}
                    onFocus={() => { if (volSuggest.length) setShowVolSuggest(true); }}
                    onBlur={() => setTimeout(() => setShowVolSuggest(false), 150)}
                    placeholder={lang === 'es' ? 'VOL-0001 o nombre...' : 'VOL-0001 or name...'} autoComplete="off" />
                  {showVolSuggest && volSuggest.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="filter-resource-suggestions">
                      {volSuggest.map(v => (
                        <button key={v.volunteer_id} type="button" data-testid={`filter-resource-suggestion-${v.volunteer_id}`}
                          onMouseDown={(e) => { e.preventDefault(); pickVolunteer(v); }}
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-[#1f3e80]/5 flex items-center justify-between gap-2">
                          <span className="truncate text-stone-700">{v.display_name || '—'}</span>
                          <span className="font-mono text-[10px] text-stone-400 shrink-0">{v.volunteer_id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.study_filter}</Label>
                <Input className="h-8 text-xs" data-testid="filter-study" value={filters.study_id} onChange={e => { setFilters(p => ({ ...p, study_id: e.target.value })); setPage(0); }} placeholder="EST-0001..." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.date_from}</Label>
                <Input type="date" className="h-8 text-xs" data-testid="filter-date-from" value={filters.date_from} onChange={e => { setFilters(p => ({ ...p, date_from: e.target.value })); setPage(0); }} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.date_to}</Label>
                <Input type="date" className="h-8 text-xs" data-testid="filter-date-to" value={filters.date_to} onChange={e => { setFilters(p => ({ ...p, date_to: e.target.value })); setPage(0); }} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lb.search}</Label>
                <div className="relative">
                  <MagnifyingGlass size={12} className="absolute left-2 top-2 text-stone-400" />
                  <Input className="h-8 text-xs pl-7" data-testid="filter-search" value={filters.search} onChange={e => { setFilters(p => ({ ...p, search: e.target.value })); setPage(0); }} placeholder={lb.search} />
                </div>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn" className="text-xs text-stone-500">{lb.clear}</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-stone-500">{lb.loading}</p></div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardText size={40} className="text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">{lb.no_records}</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
              <span className="text-xs text-stone-500 font-mono">{total} {lb.records}</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                    <ArrowLeft size={12} />
                  </Button>
                  <span className="text-xs font-mono text-stone-600">{page + 1} {lb.page_of} {totalPages}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                    <ArrowRight size={12} />
                  </Button>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">{lb.date}</TableHead>
                  <TableHead className="w-48">{lb.user}</TableHead>
                  <TableHead className="w-24">{lb.action}</TableHead>
                  <TableHead className="w-36">{lb.module}</TableHead>
                  <TableHead className="w-28">{lb.resource}</TableHead>
                  <TableHead className="w-28">{lb.ip}</TableHead>
                  <TableHead>{lb.details}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, i) => (
                  <TableRow
                    key={log.id || i}
                    data-testid={`audit-row-${i}`}
                    className="hover:bg-stone-50 cursor-pointer"
                    onClick={() => openDetail(log)}
                  >
                    <TableCell className="font-mono text-xs text-stone-600">{formatDate(log.timestamp)}</TableCell>
                    <TableCell className="text-xs text-stone-700">{log.user_email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] font-mono ${actionColors[log.action] || 'bg-stone-50 text-stone-600'}`}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-xs font-medium ${moduleIcons[log.module] || 'text-stone-600'}`}>{log.module || '-'}</TableCell>
                    <TableCell className="font-mono text-xs text-stone-600">{log.resource_id || '-'}</TableCell>
                    <TableCell className="font-mono text-[10px] text-stone-400">{log.ip_address || '-'}</TableCell>
                    <TableCell className="text-[10px] text-stone-500 font-mono max-w-[200px] truncate">
                      {log.details && Object.keys(log.details).length > 0 ? JSON.stringify(log.details) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{lb.detail_title}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {getApptId(detail) && (
                <Button variant="outline" size="sm" data-testid="audit-open-timeline-btn"
                  onClick={() => openTimeline(getApptId(detail))}
                  className="rounded-sm gap-2 text-xs w-full justify-center border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5">
                  <ClockCounterClockwise size={14} />{lang === 'es' ? 'Ver historial completo de la cita' : 'View full appointment timeline'}
                </Button>
              )}
              {getVolId(detail) && (
                <Button variant="outline" size="sm" data-testid="audit-open-vol-timeline-btn"
                  onClick={() => openVolTimeline(getVolId(detail))}
                  className="rounded-sm gap-2 text-xs w-full justify-center border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5">
                  <ClockCounterClockwise size={14} />{lang === 'es' ? 'Ver historial completo del voluntario' : 'View full volunteer timeline'}
                </Button>
              )}
              {getStudyId(detail) && (
                <Button variant="outline" size="sm" data-testid="audit-open-study-timeline-btn"
                  onClick={() => openStudyTimeline(getStudyId(detail))}
                  className="rounded-sm gap-2 text-xs w-full justify-center border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5">
                  <ClockCounterClockwise size={14} />{lang === 'es' ? 'Ver historial completo del estudio' : 'View full study timeline'}
                </Button>
              )}
              {/* Metadata grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-0.5">
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">{lb.date}</p>
                  <p className="text-sm font-mono text-stone-800">{formatDate(detail.timestamp)}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">{lb.user}</p>
                  <p className="text-sm text-stone-800">{detail.user_email}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">{lb.action}</p>
                  <Badge variant="outline" className={`text-xs font-mono ${actionColors[detail.action] || ''}`}>{detail.action}</Badge>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">{lb.module}</p>
                  <p className="text-sm font-medium text-stone-800">{detail.module}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">{lb.resource}</p>
                  <p className="text-sm font-mono text-stone-800">{detail.resource_id || '-'}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">{lb.ip}</p>
                  <p className="text-sm font-mono text-stone-600">{detail.ip_address || '-'}</p>
                </div>
              </div>

              {/* Details JSON */}
              {detail.details && Object.keys(detail.details).length > 0 && (
                <div>
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">{lb.details}</p>
                  <pre className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs font-mono text-stone-700 overflow-x-auto">{JSON.stringify(detail.details, null, 2)}</pre>
                </div>
              )}

              {/* Diff table */}
              {(detail.old_values || detail.new_values) && (
                <div>
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-2">{lb.old_values} → {lb.new_values}</p>
                  {renderDiff(detail.old_values, detail.new_values)}
                </div>
              )}

              {/* Immutable badge */}
              <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                <ShieldCheck size={14} className="text-[#1f3e80]" />
                <span className="text-[10px] text-stone-500 font-mono uppercase tracking-wider">{lb.immutable} — ID: {detail.id}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Appointment Timeline Dialog */}
      <Dialog open={!!timeline} onOpenChange={() => setTimeline(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18} className="text-[#1f3e80]" />{lang === 'es' ? 'Historial de la cita' : 'Appointment timeline'}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{timeline?.appointment_id}</DialogDescription>
          </DialogHeader>
          {timeline && (
            <div className="space-y-4">
              {timeline.appointment && (
                <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs grid grid-cols-2 gap-2" data-testid="audit-timeline-appt">
                  <div><span className="text-stone-400">{lang === 'es' ? 'Estudio' : 'Study'}: </span><span className="font-mono">{timeline.appointment.study_id || 'Qsalud'}</span></div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Voluntario' : 'Volunteer'}: </span>{timeline.appointment.volunteer_name || timeline.appointment.volunteer_id || '-'}</div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Visita' : 'Visit'}: </span>{timeline.appointment.visit_code || '-'}</div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Fecha/Hora' : 'Date/Time'}: </span>{timeline.appointment.date} {timeline.appointment.time}</div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Estado' : 'Status'}: </span>{timeline.appointment.status || '-'}</div>
                </div>
              )}
              {!timeline.appointment && !timelineLoading && (
                <p className="text-[11px] text-amber-600 italic">{lang === 'es' ? 'La cita ya no existe (eliminada). Se muestra solo el historial de auditoría.' : 'Appointment no longer exists (deleted). Showing audit history only.'}</p>
              )}
              {timelineLoading ? (
                <p className="text-sm text-stone-500">{lb.loading}</p>
              ) : timeline.events.length === 0 ? (
                <p className="text-sm text-stone-400 italic">{lang === 'es' ? 'Sin eventos de auditoría para esta cita.' : 'No audit events for this appointment.'}</p>
              ) : (
                <>
                  <div className="flex justify-end" data-testid="audit-timeline-toolbar">{renderOrderToggle('appt')}</div>
                  <ol className="relative border-l-2 border-stone-200 ml-2 space-y-4 py-1" data-testid="audit-timeline-events">
                    {orderEvents(timeline.events).map((ev, i) => (
                      <li key={ev.id || i} className="ml-4 relative" data-testid={`audit-timeline-event-${i}`}>
                        <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-[#1f3e80] border-2 border-white"></span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] font-mono ${actionColors[ev.action] || 'bg-stone-50 text-stone-600'}`}>{ev.action}</Badge>
                          <span className="text-xs font-mono text-stone-500">{formatDate(ev.timestamp)}</span>
                          <span className="text-xs text-stone-500">· {ev.user_email || '-'}</span>
                        </div>
                        <p className="text-xs text-stone-700 mt-1">{eventSummary(ev)}</p>
                        {renderApptRef(ev)}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Volunteer Timeline Dialog */}
      <Dialog open={!!volTimeline} onOpenChange={() => setVolTimeline(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-vol-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18} className="text-[#1f3e80]" />{lang === 'es' ? 'Historial del voluntario' : 'Volunteer timeline'}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{volTimeline?.volunteer_id}</DialogDescription>
          </DialogHeader>
          {volTimeline && (
            <div className="space-y-4">
              {volTimeline.volunteer && (
                <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs grid grid-cols-2 gap-2" data-testid="audit-vol-timeline-header">
                  <div><span className="text-stone-400">{lang === 'es' ? 'Voluntario' : 'Volunteer'}: </span><span className="font-mono">{volTimeline.volunteer.volunteer_id}</span></div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Nombre' : 'Name'}: </span>{volTimeline.volunteer.name || '-'}</div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Estado' : 'Status'}: </span>{volTimeline.volunteer.status || '-'}</div>
                </div>
              )}
              {!volTimeline.volunteer && !volTimelineLoading && (
                <p className="text-[11px] text-amber-600 italic">{lang === 'es' ? 'El voluntario ya no existe. Se muestra solo el historial de auditoría.' : 'Volunteer no longer exists. Showing audit history only.'}</p>
              )}
              {volTimelineLoading ? (
                <p className="text-sm text-stone-500">{lb.loading}</p>
              ) : volTimeline.events.length === 0 ? (
                <p className="text-sm text-stone-400 italic">{lang === 'es' ? 'Sin eventos de auditoría para este voluntario.' : 'No audit events for this volunteer.'}</p>
              ) : (
                <>
                  <div className="flex items-center justify-between" data-testid="audit-vol-timeline-toolbar">
                    <p className="text-[11px] text-stone-500 font-mono">{volTimeline.events.length} {lb.records}</p>
                    {renderOrderToggle('vol')}
                  </div>
                  <ol className="relative border-l-2 border-stone-200 ml-2 space-y-4 py-1" data-testid="audit-vol-timeline-events">
                    {orderEvents(volTimeline.events).map((ev, i) => (
                      <li key={ev.id || i} className="ml-4 relative" data-testid={`audit-vol-timeline-event-${i}`}>
                        <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-[#1f3e80] border-2 border-white"></span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] font-mono ${actionColors[ev.action] || 'bg-stone-50 text-stone-600'}`}>{ev.action}</Badge>
                          <span className={`text-[10px] font-medium ${moduleIcons[ev.module] || 'text-stone-500'}`}>{ev.module}</span>
                          <span className="text-xs font-mono text-stone-500">{formatDate(ev.timestamp)}</span>
                          <span className="text-xs text-stone-500">· {ev.user_email || '-'}</span>
                        </div>
                        <p className="text-xs text-stone-700 mt-1">{eventSummary(ev)}</p>
                        {renderApptRef(ev)}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Study Timeline Dialog */}
      <Dialog open={!!studyTimeline} onOpenChange={() => setStudyTimeline(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-study-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18} className="text-[#1f3e80]" />{lang === 'es' ? 'Historial del estudio' : 'Study timeline'}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{studyTimeline?.study_id}</DialogDescription>
          </DialogHeader>
          {studyTimeline && (
            <div className="space-y-4">
              {studyTimeline.study && (
                <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs grid grid-cols-2 gap-2" data-testid="audit-study-timeline-header">
                  <div><span className="text-stone-400">{lang === 'es' ? 'Estudio' : 'Study'}: </span><span className="font-mono">{studyTimeline.study.study_id}</span></div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Código' : 'Code'}: </span>{studyTimeline.study.code || '-'}</div>
                  <div className="col-span-2"><span className="text-stone-400">{lang === 'es' ? 'Título' : 'Title'}: </span>{studyTimeline.study.title || '-'}</div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Tipo' : 'Type'}: </span>{studyTimeline.study.product_type || '-'}</div>
                  <div><span className="text-stone-400">{lang === 'es' ? 'Estado' : 'Status'}: </span>{studyTimeline.study.status || '-'}</div>
                </div>
              )}
              {!studyTimeline.study && !studyTimelineLoading && (
                <p className="text-[11px] text-amber-600 italic">{lang === 'es' ? 'El estudio ya no existe. Se muestra solo el historial de auditoría.' : 'Study no longer exists. Showing audit history only.'}</p>
              )}
              {studyTimelineLoading ? (
                <p className="text-sm text-stone-500">{lb.loading}</p>
              ) : studyTimeline.events.length === 0 ? (
                <p className="text-sm text-stone-400 italic">{lang === 'es' ? 'Sin eventos de auditoría para este estudio.' : 'No audit events for this study.'}</p>
              ) : (
                <>
                  <div className="flex items-center justify-between" data-testid="audit-study-timeline-toolbar">
                    <p className="text-[11px] text-stone-500 font-mono">{studyTimeline.events.length} {lb.records}</p>
                    {renderOrderToggle('study')}
                  </div>
                  <ol className="relative border-l-2 border-stone-200 ml-2 space-y-4 py-1" data-testid="audit-study-timeline-events">
                    {orderEvents(studyTimeline.events).map((ev, i) => (
                      <li key={ev.id || i} className="ml-4 relative" data-testid={`audit-study-timeline-event-${i}`}>
                        <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-[#1f3e80] border-2 border-white"></span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] font-mono ${actionColors[ev.action] || 'bg-stone-50 text-stone-600'}`}>{ev.action}</Badge>
                          <span className={`text-[10px] font-medium ${moduleIcons[ev.module] || 'text-stone-500'}`}>{ev.module}</span>
                          <span className="text-xs font-mono text-stone-500">{formatDate(ev.timestamp)}</span>
                          <span className="text-xs text-stone-500">· {ev.user_email || '-'}</span>
                        </div>
                        <p className="text-xs text-stone-700 mt-1">{eventSummary(ev)}</p>
                        {renderApptRef(ev)}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
